import { wrapGlobal } from '../runtime/legacy.js';

/**
 * Keeps mission progress in sync with logged workouts.
 *
 * Two bugs in the legacy core this works around:
 *
 *   1. `renderMissions()` only calls `computeUserMissions()` when `userMissions`
 *      is empty, so progress freezes at whatever it was when the user logged in.
 *   2. Weekly `pr_count` missions are hardcoded to 0 progress in the compiled
 *      core, so they can never be completed.
 *
 * Both belong in the core itself. Until it is untangled, recomputing on every
 * render is correct and cheap — the workout set is already in memory.
 */

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Counts distinct exercises where a set logged this week matched or beat the
 * stored personal record.
 */
function weeklyPRCount(state) {
  const cutoff = Date.now() - WEEK_MS;
  const prs = state.personalRecords || {};
  const hit = new Set();

  for (const workout of state.workouts || []) {
    if (new Date(workout.date).getTime() < cutoff) continue;

    for (const exercise of workout.exercises || []) {
      if (hit.has(exercise.exercise_id)) continue;
      const pr = prs[exercise.exercise_id];
      if (!pr) continue;

      const beatsPR = (exercise.sets || []).some(
        (set) =>
          set.completed !== false &&
          (set.weight_kg || 0) >= pr.max_weight &&
          (set.reps || 0) >= (pr.max_weight_reps || 1),
      );
      if (beatsPR) hit.add(exercise.exercise_id);
    }
  }

  return hit.size;
}

/** Rows the core computed client-side are prefixed "um-"; the rest came from the DB. */
function isComputedRow(mission) {
  return String(mission.id).startsWith('um-');
}

function refreshMissions(state) {
  const fresh = globalThis.computeUserMissions();
  const prCount = weeklyPRCount(state);

  for (const entry of fresh) {
    const mission = entry.mission;
    if (mission?.progress_type === 'pr_count' && mission.mission_type === 'weekly') {
      entry.progress = Math.min(prCount, mission.target);
      entry.status = prCount >= mission.target ? 'completed' : 'in_progress';
    }
  }

  // Claimed/completed missions are persisted server-side — those rows win over
  // anything recomputed locally, otherwise claiming would appear to undo itself.
  const persisted = (state.userMissions || []).filter((m) => !isComputedRow(m));
  const persistedIds = new Set(
    persisted.map((m) => m.mission_id || m.mission?.id).filter(Boolean),
  );

  state.userMissions = persisted.concat(
    fresh.filter((m) => !persistedIds.has(m.mission_id)),
  );
}

export function installMissionProgress() {
  if (typeof globalThis.computeUserMissions !== 'function') return;

  wrapGlobal('renderMissions', function (next, container) {
    const state = globalThis.AppState;
    if (state?.userMissions?.length > 0) {
      refreshMissions(state);
    }
    return next(container);
  });
}
